import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { localeTag } from '@/i18n/format';
/**
 * CatchmentResultPanel – displays catchment analysis results in a collapsible
 * hierarchical panel matching the legacy catchmentLayout.html structure.
 *
 * Bottom-anchored, wider, draggable from header, resizable from edges.
 * Includes minimize/maximize toggle.
 *
 * Hierarchy:  Point ▸ Travel type ▸ Time slot ▸ Layer analysis table
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { X, ChevronRight, ChevronDown, MapPin, Car, Footprints, Bike, Circle, Minus, Maximize2 } from 'lucide-react';
import { useColumnFilters, FilterHeaderCell } from '../common/ColumnFilter';
import { useColumnTranslations, useLayerTranslations } from '../../hooks/useColumnTranslations';
import { useMapStore } from '../../store/map.store';
import { useStudio } from '../ReportStudio/store';
import { snapshotMap } from '../ReportStudio/snapshot';
import { datasetsFromAnalysis, draftReport } from '../ReportStudio/engine';
import type {
  ParsedPointData,
  TravelResult,
  TimeResult,
  AnalysisResult,
} from '../../utils/catchmentRenderer';
import { travelTypeName, getCatchmentColor } from '../../utils/catchmentRenderer';

/* ── Props ── */
interface CatchmentResultPanelProps {
  data: ParsedPointData[];
  onClose: () => void;
  onClear: () => void;
  minimized: boolean;
  onToggleMinimize: () => void;
}

/* ── Helpers ── */

interface FlatTravel {
  key: string;
  travelTypeRaw: number;
  timeResults: FlatTime[];
}
interface FlatTime {
  key: string;
  time: string;
  analysisResults: AnalysisResult[];
}

function flattenTravelResults(
  travelResults: Record<string, TravelResult> | TravelResult[] | undefined,
): FlatTravel[] {
  if (!travelResults) return [];
  const entries: FlatTravel[] = [];
  if (Array.isArray(travelResults)) {
    (travelResults as any[]).forEach((tr, idx) => {
      entries.push({
        key: tr.travelType ?? `travel-${idx}`,
        travelTypeRaw: travelTypeRawFromName(tr.travelType ?? ''),
        timeResults: flattenTimeResults(tr.timeResults, tr.times),
      });
    });
  } else {
    Object.entries(travelResults).forEach(([key, tr]) => {
      entries.push({
        key,
        travelTypeRaw: travelTypeRawFromName(key),
        timeResults: flattenTimeResults(tr.timeResults, (tr as any).times),
      });
    });
  }
  return entries;
}

function flattenTimeResults(
  timeResults: Record<string, TimeResult> | TimeResult[] | undefined,
  parentTimes?: number[],
): FlatTime[] {
  if (!timeResults) return [];
  if (Array.isArray(timeResults)) {
    return (timeResults as any[])
      .filter((t) => t && t.analysisResults)
      .map((t, i) => {
        const timeVal = t.time ?? (parentTimes?.[i] != null ? `${parentTimes[i]} min` : `${i}`);
        return {
          key: String(timeVal),
          time: String(timeVal),
          analysisResults: Array.isArray(t.analysisResults) ? t.analysisResults : [],
        };
      });
  }
  return Object.entries(timeResults)
    .filter(([, tr]) => tr && tr.analysisResults)
    .map(([key, tr]) => ({
      key,
      time: tr.time ?? key,
      analysisResults: tr.analysisResults ?? [],
    }));
}

function travelTypeRawFromName(name: string): number {
  const n = name.toLowerCase();
  if (n.includes('walk')) return 1;
  if (n.includes('vheavy') || n.includes('very heavy') || n.includes('vhvytrf')) return 7;
  if (n.includes('heavy') || n.includes('heavydt')) return 4;
  if (n.includes('no traf') || n.includes('notrf') || n.includes('notraffic')) return 6;
  if (n.includes('motor')) return 5;
  if (n.includes('ring')) return 3;
  if (n.includes('drive')) return 2;
  return 1;
}

/** Translate backend i18n keys like "global.menu.timemenu.heavydt" to display names */
function travelTypeDisplayName(key: string, raw: number): string {
  // If the key is a localization key, use the raw type for display
  if (key.startsWith('global.menu.') || key.startsWith('global.')) {
    return travelTypeName(raw);
  }
  return key || travelTypeName(raw);
}

function travelIcon(raw: number) {
  switch (raw) {
    case 1: return <Footprints size={13} />;
    case 5: return <Bike size={13} />;
    case 3: return <Circle size={13} />;
    default: return <Car size={13} />;
  }
}

const PAGE_SIZE = 50;

/* ══════════════════════════════════════════════════════════════
   Drag / Resize hook (bottom-anchored: tracks bottom, left, width, height)
   ══════════════════════════════════════════════════════════════ */
function useBottomDragResize(
  initialRect: { left: number; bottom: number; w: number; h: number },
  minSize = { w: 500, h: 200 },
) {
  const [rect, setRect] = useState(initialRect);
  const dragRef = useRef<{ startX: number; startY: number; origLeft: number; origBottom: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number; edge: string } | null>(null);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origLeft: rect.left, origBottom: rect.bottom };
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setRect((prev) => ({
        ...prev,
        left: dragRef.current!.origLeft + ev.clientX - dragRef.current!.startX,
        bottom: dragRef.current!.origBottom - (ev.clientY - dragRef.current!.startY),
      }));
    };
    const onUp = () => { dragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [rect.left, rect.bottom]);

  const onResizeStart = useCallback((edge: string) => (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: rect.w, origH: rect.h, edge };
    const origBottom = rect.bottom;
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const dx = ev.clientX - resizeRef.current.startX;
      const dy = ev.clientY - resizeRef.current.startY;
      setRect((prev) => {
        const newRect = { ...prev };
        if (resizeRef.current!.edge.includes('right')) {
          newRect.w = Math.max(minSize.w, resizeRef.current!.origW + dx);
        }
        if (resizeRef.current!.edge.includes('left')) {
          const newW = Math.max(minSize.w, resizeRef.current!.origW - dx);
          newRect.left = prev.left + (prev.w - newW);
          newRect.w = newW;
        }
        if (resizeRef.current!.edge.includes('top')) {
          newRect.h = Math.max(minSize.h, resizeRef.current!.origH - dy);
        }
        if (resizeRef.current!.edge.includes('bottom')) {
          const newH = Math.max(minSize.h, resizeRef.current!.origH + dy);
          newRect.h = newH;
          // Decrease bottom so the panel grows downward
          newRect.bottom = origBottom - (newH - resizeRef.current!.origH);
        }
        return newRect;
      });
    };
    const onUp = () => { resizeRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [rect.w, rect.h, rect.left, rect.bottom, minSize.w, minSize.h]);

  return { rect, setRect, onDragStart, onResizeStart };
}

/* ══════════════════════════════════════════════════════════════════ */
/*  Component                                                       */
/* ══════════════════════════════════════════════════════════════════ */

export const CatchmentResultPanel: React.FC<CatchmentResultPanelProps> = ({
  data,
  onClose,
  onClear,
  minimized,
  onToggleMinimize,
}) => {
  const { t } = useTranslation();

  /* ── Layer translations for BI card titles ── */
  const { t: tLayer } = useLayerTranslations();

  const initialW = Math.min(window.innerWidth * 0.85, 1100);
  const initialH = Math.min(window.innerHeight * 0.4, 420);
  const { rect, onDragStart, onResizeStart } = useBottomDragResize({
    left: Math.max(8, (window.innerWidth - initialW) / 2),
    bottom: 8,
    w: initialW,
    h: initialH,
  });

  /* Expanded state tracking */
  const [expandedPoints, setExpandedPoints] = useState<Set<number>>(() => new Set([0]));
  const [expandedTravels, setExpandedTravels] = useState<Set<string>>(() => new Set(['0-0']));
  const [expandedTimes, setExpandedTimes] = useState<Set<string>>(new Set());
  const [pages, setPages] = useState<Record<string, number>>({});

  const toggle = useCallback(
    (_set: Set<any>, key: any, setter: React.Dispatch<React.SetStateAction<Set<any>>>) => {
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [],
  );

  /* ── Report studio ── */
  const { t: tCol } = useColumnTranslations();
  const mapInstance = useMapStore((s) => s.mapInstance);
  const openDraft = useStudio((s) => s.openDraft);
  const [opening, setOpening] = useState(false);

  const allTables = useMemo(() => {
    const tables: { id: string; layerName: string; keys: string[]; features: Record<string, any>[]; pointName: string; travelType: string; time: string }[] = [];
    data.forEach((point, pi) => {
      const travels = flattenTravelResults(point.travelResults);
      travels.forEach((travel, ti) => {
        travel.timeResults.forEach((time, tmi) => {
          (time.analysisResults || []).forEach((ar, ari) => {
            if (ar.features && ar.features.length > 0) {
              tables.push({
                id: `table-${pi}-${ti}-${tmi}-${ari}`,
                layerName: ar.layerName,
                keys: ar.keys,
                features: ar.features,
                pointName: point.name || `Point ${pi + 1}`,
                travelType: travelTypeName(travel.travelTypeRaw),
                time: time.time,
              });
            }
          });
        });
      });
    });
    return tables;
  }, [data]);

  /** The results into the report studio: every table, and the map as it is now. */
  const enterFullscreen = useCallback(async () => {
    if (opening) return;
    if (useStudio.getState().doc && useStudio.getState().dirty && !window.confirm(t('studio.close_unsaved'))) return;
    setOpening(true);
    try {
      const map = await snapshotMap(mapInstance);
      const datasets = datasetsFromAnalysis(
        allTables.map((tb) => ({
          ...tb,
          title: `${tLayer(tb.layerName)} — ${data.length > 1 ? `${tb.pointName} · ` : ''}${tb.travelType} ${tb.time}`,
        })),
        tCol,
      );
      openDraft(draftReport(datasets, {
        name: t('catchment.report_header'),
        map,
        mapTitle: t('studio.kind_map'),
      }));
    } finally {
      setOpening(false);
    }
  }, [opening, mapInstance, allTables, tLayer, tCol, data.length, openDraft, t]);


  if (!data || data.length === 0) return null;

  /* ── Minimized state: just a pill button ── */
  if (minimized) {
    return (
      <button
        onClick={onToggleMinimize}
        className="fixed z-50 flex items-center gap-2 px-3 py-2 rounded-lg bg-gn-primary border border-gn shadow-xl backdrop-blur-sm text-gn-primary hover:bg-gn-secondary transition-colors"
        style={{ bottom: 12, left: rect.left }}
      >
        <Maximize2 size={14} className="text-gn-blue" />
        <span className="text-[11px] font-medium">{t('catchment.results')}</span>
      </button>
    );
  }

  return (
    <div
      className="fixed z-50 flex flex-col bg-gn-primary border border-gn rounded-t-lg shadow-2xl backdrop-blur-sm overflow-hidden"
      style={{
        left: rect.left,
        bottom: rect.bottom,
        width: rect.w,
        height: rect.h,
      }}
    >
      {/* ── Resize handle: top edge ── */}
      <div
        className="absolute top-0 left-4 right-4 h-1 cursor-n-resize z-10 hover:bg-gn-blue-light transition-colors"
        onMouseDown={onResizeStart('top')}
      />
      {/* ── Resize handle: left edge ── */}
      <div
        className="absolute top-0 left-0 bottom-0 w-1 cursor-w-resize z-10 hover:bg-gn-blue-light transition-colors"
        onMouseDown={onResizeStart('left')}
      />
      {/* ── Resize handle: right edge ── */}
      <div
        className="absolute top-0 right-0 bottom-0 w-1 cursor-e-resize z-10 hover:bg-gn-blue-light transition-colors"
        onMouseDown={onResizeStart('right')}
      />
      {/* ── Resize handle: bottom edge ── */}
      <div
        className="absolute bottom-0 left-4 right-4 h-1 cursor-s-resize z-10 hover:bg-gn-blue-light transition-colors"
        onMouseDown={onResizeStart('bottom')}
      />

      {/* ── Header (draggable) ── */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b border-gn bg-gn-secondary shrink-0 cursor-move select-none"
        onMouseDown={onDragStart}
      >
        <span className="text-xs font-semibold text-gn-primary tracking-wide">
          {t('catchment.results_title')}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={onClear}
            className="px-2 py-0.5 text-[10px] rounded bg-gn-danger-light text-gn-danger hover:bg-gn-danger-light transition-colors"
          >
            {t('buttons.clear')}
          </button>
          <button
            onClick={enterFullscreen}
            className="p-0.5 text-gn-secondary hover:text-gn-blue transition-colors"
            title={t('studio.open_report')}
            disabled={opening}
          >
            <Maximize2 size={14} />
          </button>
          <button
            onClick={onToggleMinimize}
            className="p-0.5 text-gn-secondary hover:text-gn-primary transition-colors"
            title={t('report.minimize')}
          >
            <Minus size={14} />
          </button>
          <button onClick={onClose} className="p-0.5 text-gn-secondary hover:text-gn-primary transition-colors">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* ── Scrollable body ── */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1">
        {data.map((point, pi) => {
          const pointExpanded = expandedPoints.has(pi);
          const travels = flattenTravelResults(point.travelResults);

          return (
            <div key={pi} className="rounded border border-gn-light bg-gn-tertiary">
              {/* Point header */}
              <button
                onClick={() => toggle(expandedPoints, pi, setExpandedPoints)}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-gn-tertiary transition-colors"
              >
                {pointExpanded ? <ChevronDown size={12} className="text-gn-secondary" /> : <ChevronRight size={12} className="text-gn-secondary" />}
                <MapPin size={12} className="text-gn-blue" />
                <span className="text-[11px] font-medium text-gn-primary truncate">
                  {point.name || `Point ${pi + 1}`}
                </span>
              </button>

              {pointExpanded && (
                <div className="px-2 pb-2 space-y-1">
                  {/* Point local statistics */}
                  {point.pointStatistics?.localAdministrativeInfo?.localInformation && (
                    <LocalInfoSection data={point.pointStatistics.localAdministrativeInfo.localInformation} />
                  )}

                  {/* Nearest points */}
                  {point.pointStatistics?.NearestPoints?.analysisResults?.length > 0 && (
                    <NearestPointsSection data={point.pointStatistics.NearestPoints.analysisResults} />
                  )}

                  {/* Travel types */}
                  {travels.map((travel, ti) => {
                    const tKey = `${pi}-${ti}`;
                    const travelExpanded = expandedTravels.has(tKey);
                    const color = getCatchmentColor(travel.travelTypeRaw, 0);

                    return (
                      <div key={tKey} className="rounded border border-gn-light bg-gn-tertiary">
                        <button
                          onClick={() => toggle(expandedTravels, tKey, setExpandedTravels)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-gn-tertiary transition-colors"
                        >
                          {travelExpanded ? <ChevronDown size={11} className="text-gn-secondary" /> : <ChevronRight size={11} className="text-gn-secondary" />}
                          <span style={{ color }}>{travelIcon(travel.travelTypeRaw)}</span>
                          <span className="text-[11px] font-medium text-gn-primary">
                            {travelTypeDisplayName(travel.key, travel.travelTypeRaw)}
                          </span>
                        </button>

                        {travelExpanded && (
                          <div className="px-2 pb-1.5 space-y-1">
                            {travel.timeResults.map((time, timeIdx) => {
                              const tmKey = `${tKey}-${timeIdx}`;
                              const timeExpanded = expandedTimes.has(tmKey);

                              return (
                                <div key={tmKey} className="rounded border border-gn-light bg-gn-tertiary">
                                  <button
                                    onClick={() => toggle(expandedTimes, tmKey, setExpandedTimes)}
                                    className="w-full flex items-center gap-2 px-2 py-1 text-left hover:bg-gn-tertiary transition-colors"
                                  >
                                    {timeExpanded ? <ChevronDown size={10} className="text-gn-secondary" /> : <ChevronRight size={10} className="text-gn-secondary" />}
                                    <span
                                      className="inline-block w-2 h-2 rounded-full shrink-0"
                                      style={{ backgroundColor: getCatchmentColor(travel.travelTypeRaw, timeIdx) }}
                                    />
                                    <span className="text-[10px] text-gn-secondary">{time.time}</span>
                                  </button>

                                  {timeExpanded && time.analysisResults.length > 0 && (
                                    <div className="px-1.5 pb-1.5 space-y-1">
                                      {time.analysisResults.map((ar, ari) => (
                                        <AnalysisTable
                                          key={ari}
                                          result={ar}
                                          pageKey={`${tmKey}-${ari}`}
                                          currentPage={pages[`${tmKey}-${ari}`] ?? 0}
                                          onPageChange={(p) => setPages((prev) => ({ ...prev, [`${tmKey}-${ari}`]: p }))}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Sub-components ── */

const LocalInfoSection: React.FC<{ data: AnalysisResult[] }> = ({ data }) => (
  <div className="space-y-1">
    <div className="text-[10px] font-semibold text-gn-secondary uppercase tracking-wider px-1">
      {i18n.t('catchment.local_info')}
    </div>
    {data.map((ar, i) => (
      <MiniTable key={i} result={ar} />
    ))}
  </div>
);

const NearestPointsSection: React.FC<{ data: AnalysisResult[] }> = ({ data }) => (
  <div className="space-y-1">
    <div className="text-[10px] font-semibold text-gn-secondary uppercase tracking-wider px-1">
      {i18n.t('catchment.nearest')}
    </div>
    {data.map((ar, i) => (
      <MiniTable key={i} result={ar} />
    ))}
  </div>
);

const MiniTable: React.FC<{ result: AnalysisResult }> = ({ result }) => {
  const { t: tCol } = useColumnTranslations();
  const { t: tLayer } = useLayerTranslations();
  const visibleKeys = useMemo(
    () => result.keys.filter((k) => k !== 'geom'),
    [result.keys],
  );

  if (!result.features || result.features.length === 0) return null;

  return (
    <div className="rounded border border-gn-light bg-gn-tertiary overflow-hidden">
      <div className="flex items-center justify-between px-2 py-0.5 bg-gn-tertiary">
        <span className="text-[10px] text-gn-primary font-medium">{tLayer(result.layerName)}</span>
        <span className="text-[9px] text-gn-blue bg-gn-blue-light px-1 rounded">{result.features.length}</span>
      </div>
      <div className="overflow-auto max-h-[45vh]">
        <table className="w-full text-[9px]">
          <thead>
            <tr>
              {visibleKeys.map((k) => (
                <th key={k} className="sticky top-0 z-10 px-1.5 py-0.5 text-left text-gn-secondary font-medium whitespace-nowrap bg-gn-secondary">
                  {tCol(k)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.features.slice(0, 5).map((feat, fi) => (
              <tr key={fi} className="border-t border-gn-light hover:bg-gn-tertiary">
                {visibleKeys.map((k) => (
                  <td key={k} className="px-1.5 py-0.5 text-gn-primary whitespace-nowrap">
                    {formatCellValue(feat[k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface AnalysisTableProps {
  result: AnalysisResult;
  pageKey: string;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const AnalysisTable: React.FC<AnalysisTableProps> = ({
  result,
  currentPage,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const { t: tCol } = useColumnTranslations();
  const { t: tLayer } = useLayerTranslations();
  const visibleKeys = useMemo(
    () => result.keys.filter((k) => k !== 'geom'),
    [result.keys],
  );

  const isCatchmentAnalysis = result.layerName?.toLowerCase().includes('analysis')
    || result.layerName?.toLowerCase().startsWith('catchment');

  /* ── Column filtering (normal tables only) ── */
  const featuresForFilter = useMemo(() => result.features ?? [], [result.features]);
  const { filters, setFilter, clearFilters, hasActiveFilters, filteredFeatures, stringColumns } =
    useColumnFilters(featuresForFilter, visibleKeys);

  const displayFeatures = isCatchmentAnalysis ? (result.features ?? []) : filteredFeatures;
  const totalPages = Math.ceil(displayFeatures.length / PAGE_SIZE);
  const pageFeatures = useMemo(
    () => displayFeatures.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [displayFeatures, currentPage],
  );

  // Reset page when filters change
  useEffect(() => {
    onPageChange(0);
  }, [filters]);

  if (!result.features || result.features.length === 0) return null;

  return (
    <div className="rounded border border-gn-light bg-gn-tertiary overflow-hidden">
      {/* Layer header */}
      <div className="flex items-center justify-between px-2 py-0.5 bg-gn-tertiary">
        <span className="text-[10px] text-gn-primary font-medium">{tLayer(result.layerName)}</span>
        <span className="text-[9px] text-gn-blue bg-gn-blue-light px-1 rounded">
          {hasActiveFilters && !isCatchmentAnalysis
            ? `${filteredFeatures.length} / ${result.features.length}`
            : result.features.length}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-auto max-h-[45vh]">
        {isCatchmentAnalysis ? (
          <table className="w-full text-[9px]">
            <thead>
              <tr>
                <th className="sticky top-0 z-10 px-1.5 py-0.5 text-left text-gn-secondary font-medium bg-gn-secondary">{t('catchment.column')}</th>
                {pageFeatures.map((feat, fi) => (
                  <th key={fi} className="sticky top-0 z-10 px-1.5 py-0.5 text-left text-gn-secondary font-medium whitespace-nowrap bg-gn-secondary">
                    {feat[visibleKeys[0]] ?? fi}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleKeys.slice(1).map((key) => (
                <tr key={key} className="border-t border-gn-light hover:bg-gn-tertiary">
                  <td className="px-1.5 py-0.5 text-gn-secondary font-medium whitespace-nowrap">{tCol(key)}</td>
                  {pageFeatures.map((feat, fi) => (
                    <td key={fi} className="px-1.5 py-0.5 text-gn-primary whitespace-nowrap">
                      {formatCellValue(feat[key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-[9px]">
            <thead>
              <tr>
                {visibleKeys.map((k) => (
                  <FilterHeaderCell
                    key={k}
                    columnName={k}
                    isFilterable={stringColumns.has(k)}
                    filterValue={filters[k] || ''}
                    onFilterChange={setFilter}
                    className="sticky top-0 z-10 px-1.5 py-0.5 text-left text-gn-secondary font-medium whitespace-nowrap bg-gn-secondary"
                  >
                    {tCol(k)}
                  </FilterHeaderCell>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageFeatures.map((feat, fi) => (
                <tr key={fi} className="border-t border-gn-light hover:bg-gn-tertiary">
                  {visibleKeys.map((k) => (
                    <td key={k} className="px-1.5 py-0.5 text-gn-primary whitespace-nowrap">
                      {formatCellValue(feat[k])}
                    </td>
                  ))}
                </tr>
              ))}
              {pageFeatures.length === 0 && (
                <tr>
                  <td colSpan={visibleKeys.length} className="px-4 py-3 text-center text-[9px] text-gn-secondary italic">
                    {t('info.no_match')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer: filter info + pagination */}
      {(totalPages > 1 || hasActiveFilters) && (
        <div className="flex items-center justify-center gap-1 px-2 py-1 border-t border-gn-light">
          {hasActiveFilters && !isCatchmentAnalysis && (
            <button onClick={clearFilters} className="text-[9px] text-gn-blue hover:text-gn-blue mr-1">
              {t('table.clear_filters')}
            </button>
          )}
          {totalPages > 1 && (
            <>
              <button
                disabled={currentPage === 0}
                onClick={() => onPageChange(currentPage - 1)}
                className="px-1.5 py-0.5 text-[9px] rounded bg-gn-tertiary text-gn-secondary hover:bg-gn-hover disabled:opacity-30"
              >
                ‹
              </button>
              <span className="text-[9px] text-gn-secondary">
                {currentPage + 1} / {totalPages}
              </span>
              <button
                disabled={currentPage >= totalPages - 1}
                onClick={() => onPageChange(currentPage + 1)}
                className="px-1.5 py-0.5 text-[9px] rounded bg-gn-tertiary text-gn-secondary hover:bg-gn-hover disabled:opacity-30"
              >
                ›
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

function formatCellValue(val: any): string {
  if (val == null) return '';
  if (typeof val === 'number') {
    if (Number.isInteger(val)) return val.toLocaleString(localeTag());
    return val.toLocaleString(localeTag(), { maximumFractionDigits: 2 });
  }
  return String(val);
}

export default CatchmentResultPanel;
